/**
 * Reset password page — /reset-password
 *
 * Receives `token` query param from Better-Auth reset email link.
 * Submits new password via Better-Auth reset endpoint.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { Button } from "@better-bookkeeping/ui/button";
import { Label } from "@better-bookkeeping/ui/label";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { authClient } from "@/lib/auth-client";
import { PasswordStrengthMeter } from "@/components/password-strength-meter";
import { PasswordInput } from "@better-bookkeeping/ui/password-input";

export const Route = createFileRoute("/reset-password")({
  validateSearch: z.object({
    token: z.string().optional(),
    redirect_uri: z.string().optional(),
  }).parse,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token, redirect_uri } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="space-y-6">
        <div className="space-y-1.5">
          <h2
            className="text-2xl font-semibold tracking-tight"
            data-testid="reset-password-expired-title"
          >
            This link has expired
          </h2>
          <p className="text-sm text-muted-foreground">
            Reset links are good for one use within a short window. Request a new one to continue.
          </p>
        </div>
        <Button
          onClick={() => navigate({ to: "/forgot-password" })}
          className="w-full"
          data-testid="reset-password-request-new-link-button"
        >
          Send a new reset link
        </Button>
      </div>
    );
  }

  const form = useForm({
    defaultValues: { password: "", confirmPassword: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);

        if (value.password !== value.confirmPassword) {
          setError("Those don't match. Try retyping the second one.");
          return;
        }

        const { error: resetError } = await authClient.resetPassword({
          newPassword: value.password,
          token,
        });

        if (resetError) {
          if (resetError.status === 429 || resetError.message?.includes("429")) {
            setError("Too many tries. Take a breath and try again in a minute.");
          } else {
            setError("We couldn't reset your password. Try a fresh reset link.");
          }
          return;
        }

        // Redirect to login after successful reset
        navigate({
          to: "/login",
          search: redirect_uri ? { redirect_uri } : {},
        });
      } catch (err) {
        console.error("Password reset failed:", err);
        setError("Something went wrong. Please try again.");
      }
    },
  });

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight" data-testid="reset-password-title">
          Choose a new password
        </h2>
        <p className="text-sm text-muted-foreground">At least 12 characters. Longer is stronger.</p>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="reset-password-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="space-y-4"
        data-testid="reset-password-form"
      >
        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <form.Field
            name="password"
            validators={{ onChange: z.string().min(12, "Use at least 12 characters.") }}
            children={(field) => (
              <div className="space-y-2">
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="At least 12 characters"
                  autoFocus
                  required
                  data-testid="reset-password-password-input"
                />
                <PasswordStrengthMeter
                  value={field.state.value}
                  data-testid="reset-password-password-strength-section"
                />
                {field.state.meta.errors.length > 0 && field.state.value.length > 0 && (
                  <p
                    className="text-sm text-destructive"
                    data-testid="reset-password-password-error"
                  >
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <form.Field
            name="confirmPassword"
            validators={{ onChange: z.string().min(1, "Please confirm your password") }}
            children={(field) => (
              <div>
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  data-testid="reset-password-confirm-password-input"
                />
                {field.state.meta.errors.length > 0 && (
                  <p
                    className="text-sm text-destructive mt-1"
                    data-testid="reset-password-confirm-password-error"
                  >
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="w-full"
              data-testid="reset-password-submit-button"
            >
              {isSubmitting ? "Saving…" : "Save new password"}
            </Button>
          )}
        />
      </form>
    </div>
  );
}
